/**
 * ability-test-panel.tsx
 *
 * Generic, scalable testing interface for any Asset Ability
 * - Works with any ability structure (not hardcoded to specific abilities)
 * - Dynamically discovers actions and renders appropriate test UI
 * - Server handles all validation, gating, permissions
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { AssetWithPermission } from '@/services/asset-service';
import { Loader2, Copy, Check } from 'lucide-react';

type Props = {
  assetId: string;
  asset?: AssetWithPermission | null;
  serviceSlug?: string;
  orgSlug?: string;
  // Raw ability data from API (generic structure)
  abilities?: Record<string, unknown>;
};

interface StreamingResponse {
  response: string;
  p?: string; // Position/pointer from Cloudflare AI API
}

interface AbilityTestState {
  input: string;
  streaming: boolean;
  loading: boolean;
  response: string;
  copied: boolean;
  responseTime?: number; // in milliseconds
}

export const AbilityTestPanel = ({ asset, serviceSlug, orgSlug, abilities }: Props) => {
  const [testStates, setTestStates] = useState<Record<string, AbilityTestState>>({});
  const [selectedDependency, setSelectedDependency] = useState<string>('');

  // Extract dependencies from asset data
  const dependencies = (() => {
    if (!asset?.data || typeof asset.data !== 'object') return [];
    const deps = (asset.data as Record<string, unknown>).dependencies;
    return deps && typeof deps === 'object' ? Object.keys(deps as Record<string, unknown>) : [];
  })();

  // Get all actions from ability config by finding *ActionId properties
  const extractActions = (abilityConfig: unknown): Record<string, string> => {
    if (!abilityConfig || typeof abilityConfig !== 'object') return {};

    const actions: Record<string, string> = {};
    const config = abilityConfig as Record<string, unknown>;

    for (const [key, value] of Object.entries(config)) {
      if (key.endsWith('ActionId') && typeof value === 'string') {
        // Convert "searchActionId" -> "search", "answerActionId" -> "answer"
        const actionName = key.replace('ActionId', '');
        actions[actionName] = value;
      }
    }
    return actions;
  };

  // Check if ability is enabled
  const isEnabled = (abilityConfig: unknown): boolean => {
    if (!abilityConfig || typeof abilityConfig !== 'object') return false;
    return (abilityConfig as Record<string, unknown>).enabled === true;
  };

  // Test an action
  const testAction = async (abilityName: string, actionName: string, actionId: string, input?: string) => {
    if (!serviceSlug || !orgSlug) {
      toast.error('Service context not available. Tests must be run from a published service.');
      return;
    }

    const stateKey = `${abilityName}:${actionName}`;
    const currentState = testStates[stateKey] || { input: '', streaming: true, loading: false, response: '', copied: false };
    const state = { ...currentState, loading: true, response: '', responseTime: undefined };
    setTestStates(prev => ({ ...prev, [stateKey]: state }));

    const startTime = performance.now();

    try {
      const endpoint = `/api/svc/${orgSlug}/${serviceSlug}/${actionId}${input ? `/${input}` : ''}`;
      const method = input ? 'GET' : 'POST';
      const body = !input && state.input ? JSON.stringify({ query: state.input, stream: state.streaming }) : undefined;

      const response = await fetch(endpoint, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        credentials: 'include',
        body,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `Request failed with status ${response.status}`);
      }

      // Handle streaming response word-by-word (Server-Sent Events format)
      // Only use streaming if response is actually an event stream
      const isEventStream = response.headers.get('content-type')?.includes('event-stream');
      if (state.streaming && response.body && method === 'POST' && isEventStream) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');

            // Process complete lines
            for (let i = 0; i < lines.length - 1; i++) {
              const line = lines[i].trim();
              if (!line) continue;

              // Handle Server-Sent Events format: "data: {...}"
              let dataStr = line;
              if (line.startsWith('data: ')) {
                dataStr = line.substring(6); // Remove "data: " prefix
              }

              try {
                const data: StreamingResponse = JSON.parse(dataStr);
                if (data.response) {
                  setTestStates(prev => ({
                    ...prev,
                    [stateKey]: { ...prev[stateKey]!, response: prev[stateKey]!.response + data.response }
                  }));
                }
              } catch {
                // Skip malformed lines
              }
            }

            // Keep incomplete line in buffer
            buffer = lines[lines.length - 1];
          }

          // Process any remaining buffer
          if (buffer.trim()) {
            let dataStr = buffer.trim();
            if (dataStr.startsWith('data: ')) {
              dataStr = dataStr.substring(6);
            }
            try {
              const data: StreamingResponse = JSON.parse(dataStr);
              if (data.response) {
                setTestStates(prev => ({
                  ...prev,
                  [stateKey]: { ...prev[stateKey]!, response: prev[stateKey]!.response + data.response }
                }));
              }
            } catch {
              // Ignore parsing errors
            }
          }
        } finally {
          reader.releaseLock();
        }
      } else {
        // Non-streaming response
        try {
          const data = await response.json();
          setTestStates(prev => ({
            ...prev,
            [stateKey]: { ...prev[stateKey]!, response: JSON.stringify(data, null, 2) }
          }));
        } catch {
          // Response is not JSON (e.g., file content from content-resolution ability)
          const size = response.headers.get('content-length');
          const disposition = response.headers.get('content-disposition');
          let filename = 'file';
          if (disposition) {
            const match = disposition.match(/filename="?([^"]*)"?/);
            if (match) filename = match[1];
          }
          const sizeStr = size ? ` (${parseInt(size) > 1024 ? (parseInt(size) / 1024).toFixed(2) + ' KB' : size + ' B'})` : '';
          setTestStates(prev => ({
            ...prev,
            [stateKey]: { ...prev[stateKey]!, response: `File: ${filename}${sizeStr}` }
          }));
        }
      }

      const responseTime = performance.now() - startTime;
      setTestStates(prev => ({
        ...prev,
        [stateKey]: { ...prev[stateKey]!, responseTime }
      }));
      toast.success(`${actionName} test completed`);
    } catch (error) {
      console.error('Test failed:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      const responseTime = performance.now() - startTime;
      toast.error(errorMsg);
      setTestStates(prev => ({
        ...prev,
        [stateKey]: { ...prev[stateKey]!, response: `Error: ${errorMsg}`, responseTime }
      }));
    } finally {
      setTestStates(prev => ({
        ...prev,
        [stateKey]: { ...prev[stateKey]!, loading: false }
      }));
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, stateKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setTestStates(prev => ({
        ...prev,
        [stateKey]: { ...prev[stateKey]!, copied: true }
      }));
      setTimeout(() => {
        setTestStates(prev => ({
          ...prev,
          [stateKey]: { ...prev[stateKey]!, copied: false }
        }));
      }, 2000);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Ability Testing</CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Test your abilities with real queries and see responses
          </p>
        </CardHeader>
        <CardContent className="space-y-8">
          {Object.entries(abilities || {}).length === 0 ? (
            <div className="p-4 bg-muted rounded text-sm text-muted-foreground">
              No abilities configured for this asset.
            </div>
          ) : (
            Object.entries(abilities || {}).map(([abilityName, abilityConfig]) => {
              const enabled = isEnabled(abilityConfig);
              const actions = extractActions(abilityConfig);

              return (
                <section key={abilityName} className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold capitalize">{abilityName}</h3>
                    {!enabled && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                        ⚠️ This ability is disabled. Enable it in the Configuration tab to run tests.
                      </div>
                    )}
                    {enabled && Object.keys(actions).length === 0 && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                        ℹ️ No actions configured for this ability.
                      </div>
                    )}
                  </div>

                  {enabled && Object.entries(actions).map(([actionName, actionId]) => {
                    const stateKey = `${abilityName}:${actionName}`;
                    const state = testStates[stateKey] || { input: '', streaming: true, loading: false, response: '', copied: false };

                    // Detect if this action needs input (based on property names)
                    const needsQueryInput = actionName.includes('search') || actionName.includes('answer');
                    const needsDependencyInput = actionName.includes('dependency') || actionName.includes('registry');
                    // Only answer action supports streaming, not search
                    const supportsStreaming = actionName.includes('answer');

                    return (
                      <div key={stateKey} className="space-y-3 p-3 bg-muted/30 rounded border">
                        <Label className="text-sm font-medium capitalize">{actionName} (ID: {actionId})</Label>

                        {needsQueryInput && (
                          <>
                            <Textarea
                              placeholder={`Enter your ${actionName} query...`}
                              value={state.input}
                              onChange={(e) =>
                                setTestStates(prev => ({
                                  ...prev,
                                  [stateKey]: { ...(prev[stateKey] || { input: '', streaming: true, loading: false, response: '', copied: false }), input: e.target.value }
                                }))
                              }
                              className="min-h-20"
                              disabled={state.loading}
                            />
                            {supportsStreaming && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`stream-${stateKey}`}
                                  checked={state.streaming}
                                  onCheckedChange={(v) =>
                                    setTestStates(prev => ({
                                      ...prev,
                                      [stateKey]: { ...(prev[stateKey] || { input: '', streaming: true, loading: false, response: '', copied: false }), streaming: !!v }
                                    }))
                                  }
                                  disabled={state.loading}
                                />
                                <Label htmlFor={`stream-${stateKey}`} className="cursor-pointer text-sm">
                                  Enable streaming
                                </Label>
                              </div>
                            )}
                          </>
                        )}

                        {needsDependencyInput && (
                          <Select value={selectedDependency} onValueChange={setSelectedDependency}>
                            <SelectTrigger disabled={state.loading || dependencies.length === 0}>
                              <SelectValue placeholder="Select a dependency..." />
                            </SelectTrigger>
                            <SelectContent>
                              {dependencies.length === 0 ? (
                                <div className="p-2 text-sm text-muted-foreground">No dependencies found</div>
                              ) : (
                                dependencies.map((dep) => (
                                  <SelectItem key={dep} value={dep}>
                                    {dep}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => testAction(abilityName, actionName, actionId, needsDependencyInput ? selectedDependency : undefined)}
                            disabled={state.loading || (needsQueryInput && !state.input.trim()) || (needsDependencyInput && !selectedDependency)}
                            variant="outline"
                            size="sm"
                          >
                            {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Test {actionName}
                          </Button>
                          {state.responseTime !== undefined && (
                            <span className="text-xs text-muted-foreground">
                              {state.responseTime.toFixed(0)}ms
                            </span>
                          )}
                        </div>

                        {state.response && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Label className="text-xs font-medium">Response</Label>
                                {state.responseTime !== undefined && (
                                  <span className="text-xs text-muted-foreground">
                                    ({state.responseTime.toFixed(0)}ms)
                                  </span>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(state.response, stateKey)}
                              >
                                {state.copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                              </Button>
                            </div>
                            <pre className="bg-muted p-2 rounded overflow-auto max-h-48 text-xs whitespace-pre-wrap break-words">
                              {state.response}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {enabled && <Separator />}
                </section>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};
